import { useEffect, useState, createContext, useRef } from "react";

import { useBeforeunload } from 'react-beforeunload';
import SessionStorageService from "@/util/session-storage-service";
import { Autosave, useAutosave } from 'react-autosave';
import css from "./styles.module.scss";
import APIService from "@/util/api-service";

import useError from "@/lib/useError";
import useNotification from "@/lib/useNotification";
import useSimpleValidator from "@/lib/useSimpleValidator";

import WillEditorView1 from "./views/1";
import WillEditorView2 from "./views/2";
import WillEditorView3 from "./views/3";
import WillEditorView4 from "./views/4";
import WillEditorView5 from "./views/5";
import WillEditorView6 from "./views/6";
import WillEditorViewWillRegistration from "./views/will-registration";
import WillEditorSummary from "./views/summary";

import ProgressBar from "./components/progress-bar";
import Navigation from "./components/navigation";

import { WillFields, WillTypes } from "@/types";


export const WillEditorContext = createContext<any>(null);

export const fieldLabels:WillFields = {
    'name':         'Etunimet ja Sukunimi',
    'ssn':          'Henkilötunnus',
    'city':         'Paikkakunta',
    'postalCode':   'Postinumero',
    'address':      'Osoite',
    'email':        'Sähköposti',
    'phone':        'Puhelinnumero',
    'contactPeople': [
        {
            'name':         'Etunimet ja Sukunimi',
            'email':        'Sähköposti',
            'phone':        'Puhelinnumero',
        }
    ],
    'how': {
        'value':    'Miten?',
        'subField': 'Missä?'
    },
    'burialInfo__freeText':                 'Täydentäviä tietoja hautauksesta',
    'burialInfo__coffin':                   'Arkun materiaali ja väri, arkkukukat',
    'burialInfo__stone':                    'Uusi kivi, erillinen laatta hautaan, nimen kaiverrus hautakiveen.',
    'burialInfo__ceremony__where':          'Missä',
    'burialInfo__ceremony__how':            'Miten (kirkollinen, muu)',
    'burialInfo__memorialService__where':   'Missä',
    'burialInfo__memorialService__how':     'Miten (laulut, musiikki, kukat, ruoka)',
    'burialInfo__obituary':                 'Toiveet (missä lehdessä ilmoitetaan)',
    'burialInfo__otherWishes':              'Muita toiveita',
    'documents__where':                     'Missä säilytät tärkeitä omaisuuteen liittyviä asiakirjoja?',
    'documents__what':                      'Mitä asiakirjoja?',
    'otherNotes':                           'Muuta huomioitavaa',
    'willRegistration__where':              'Kuvaile testamenttisi säilytyspaikka.',
    'willRegistration__moreInfo':           'Muu tärkeä tieto testamenttiin liittyen (vapaaehtoinen)'
}   

// main headings used in editor views
export const viewHeadings = [
    'Henkilötiedot',
    'Kuka hoitaa hautajaisiin liittyvät asiat?',
    'Minne haudataan?',
    'Lisätietoa hautauksesta',
    'Asiakirjojen säilytys',
    'Muuta huomioitavaa'
];

// Headings for Hautajaissuunnitelma sections in view 4
export const burialPlannerHeadings = [
    'Arkku',
    'Hautakivi',
    'Siunaustilaisuus',
    'Muistotilaisuus',
    'Kuolinilmoitus',
    'Muita toiveita'
];

type Props = {
    willType: WillTypes,
    will: any,
    setWill: any,
    onFinished: () => void
}


const WillEditor = ( props:Props ) => {
    
    const { will, setWill } = props;  

    const [view, setView] = useState(0);

    const [generatingWill, setGeneratingWill] = useState(false);
    const [ autoSaving, setAutoSaving ] = useState(false);

    const { addNotification, Notification } = useNotification();

    // Validator for simple fields
    const simpleValidator = useSimpleValidator();
    // Since the contact people fields are dynamic need to use different validator instance
    const contactPeopleSimpleValidator = useSimpleValidator();

    const { addError } = useError();

    // delete orderID session storage when this is first mounted. same with search tool
    useEffect( () => {
        SessionStorageService.removeOrderID(props.willType);
    }, []);

    /*
    *   update singular field value in will state object 
    */
    const setWillField = ( key:string, value:string ) => {
        try {

            // Show validation error for current field
            simpleValidator.current.showMessageFor(key)

            let state = {...will}
            state['fields'][key] = value;
                 
            setWill(state);
            
        } catch( error:any ) {
        
            addError( error );
        }
    }
    const onChangeHandler = ( e:any ) => {
        setWillField( e.target?.name, e.currentTarget.value );
    }



    /*
    *   Autosave update will to session storage
    */
    useAutosave({ data: will, onSave: ( data: any ) => { 
        
        if ( ! generatingWill && ! autoSaving ) {
            setAutoSaving( true );
            try {
                SessionStorageService.setWill( data, props.willType );
                APIService.postWithUidToken(`/api/hautajaistestamentti/update/${props.willType.toLowerCase()}`, data).then( (response) => {
                    setAutoSaving( false );
                    if ( response.error ) {
                        addNotification('ERROR');
                    }
                }); 
                
            }
            catch( error:any ) {
                addError(error); 
            } 
        }

    }});




    /*
    *   Generate will PDF
    */
    const generateWill = () => {

        if ( generatingWill ) {
            return;
        }
        setGeneratingWill(true);

        // Validate every field that needs it, dont proceed with generation if fails. Instead show all validation error messages.
       

        if ( ! simpleValidator.current.allValid() || ! contactPeopleSimpleValidator.current.allValid() ) {
            simpleValidator.current.showMessages();
            contactPeopleSimpleValidator.current.showMessages();
            setGeneratingWill(false);
            addNotification('VALIDATION_ERROR');
            return;
        }

        APIService.postWithUidToken( `/api/hautajaistestamentti/generate/${props.willType.toLowerCase()}`, will )
        .then( ( will ) => {    
            if ( ! will.error ) {  
                // Remove cached will 
                SessionStorageService.removeWill( props.willType );
                props.onFinished(); 
            } else {
                addNotification('ERROR'); 
                setGeneratingWill(false);
            }
        })
        .catch( ( error ) => {
            addNotification('ERROR');
            setGeneratingWill(false);
        })

    }

    // Scroll to top of page when view is changed
    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
      }, [view]);

    

    /*
    *   Views
    */
    const views:JSX.Element[] = [];
    
    if ( props.willType == 'WILL' ) {
        views.push( <div className={css.view}><WillEditorView1 /></div> );
        views.push( <div className={css.view}><WillEditorView2 simpleValidator={contactPeopleSimpleValidator} addNotification={addNotification} /></div> );
        views.push( <div className={css.view}><WillEditorView3 /></div> );
        views.push( <div className={css.view}><WillEditorView4 /></div> );
        views.push( <div className={css.view}><WillEditorView5 /></div> ); 
        views.push( <div className={css.view}><WillEditorView6 /></div> ); 
    }

    else if ( props.willType == 'WILL_REGISTRATION' ) {
        views.push( <div className={css.view}><WillEditorView1 /></div> );
        views.push( <div className={css.view}><WillEditorViewWillRegistration /></div> );
    }

    views.push(<div className={css['view--wide']}><WillEditorSummary willType={props.willType} setView={setView} /></div>);

    return(
        <WillEditorContext.Provider value={{ will, onChangeHandler, setWillField, css, simpleValidator }}>
            <div className={css.willEditor}> 
                <Notification />
                <div className={css.views}>
                    {views[view]}
                </div>
                <div className={css.navigation}>
                    <ProgressBar views={views} currentView={view}/>
                    <Navigation views={views} 
                                currentView={view} 
                                setView={setView}
                                generateWill={generateWill}
                                generatingWill={generatingWill}
                                willType={props.willType}
                    />
                </div>
            </div>                
        </WillEditorContext.Provider> 
    );
    

}

export default WillEditor;